<script lang="ts" setup>
import { computed, onMounted, reactive, ref, watch } from "vue";
import AUApi from "@/api";
import { getSiteData } from "@/NavigationHelper";
import {
  getKennungValueDisplay,
  getKZValueDisplay,
  getValue,
} from "@/DataHelper";
import { onShowSite, goBack } from "@/NavigationHelper";
import { begriffBezeichnung, globalsBegriffMemo } from "@/local";
import {
  mountedBase,
  BaseSetup,
  loadImage,
  deleteStt,
} from "@/DataLoaderStatic";
import SpeedDial from "@/components/SpeedDial.vue";
import BaseEditor from "@/components/BaseEditor.vue";
import BaseBenutzerMitarbeiter from "./BenutzerMitarbeiter.vue";
import { Globals } from "@/Globals";
import Constants from "@/Constants";
import { isMobile } from "@syncfusion/ej2-vue-schedule/index.js";

const emit = defineEmits(["back"]);

let stammQuery: string = "";
let auswahlQuery = ref<string>("");
let neuDialog!: string;
let showUser = ref<boolean>(false);
let dialogOpen = ref<boolean>(false);
let myHeight = ref<number>(window.innerHeight);
// Berechne die verfügbare Höhe für die Komponente (abzüglich Toolbar etc.)
const contentHeight = computed(() => myHeight.value - 64 - 48); // Beispiel: 64px Toolbar, 48px weitere Abstände

let initalized = ref<boolean>(false);
let showBenutzer = ref<boolean>(false);
let mini = ref<boolean>(true);
let drawer = ref<boolean>(true);
let wasopen = false;
let showData = ref<boolean>(false);
let selecteditem = ref<number>(0);
let lastSelectedTab = ref<number | null>(null);
let stammBezeichnung = ref<string>("");
let stammKennung = ref<string>("");
let saveVisibleStt: boolean = false;
let dialogDelete = ref<boolean>(false);
let avatar = ref<string>("");
let selected = ref<number>(0);
let step = ref<number>(0);
let comboKey = ref<number>(0);
let neuErfassung = ref<boolean>(true);
let allowDelete = ref<boolean>(true);
let showTimeField = ref<boolean>(true);
let emitReload = ref<boolean>(false);

const mobile = ref(false);
const siteData = getSiteData();

let saveFrage = ref<boolean>(false);
let frageDelete: string = globalsBegriffMemo(
  Globals.Begriff,
  "Loeschen2",
  "Soll der Datensatz wirklich gelöscht werden?",
);
let readOnlyMutable = ref<boolean>(false);
let dialogStt: any = {
  aic: 0,
  title: "--!!--",
  componentName: "None",
  zeitbereich: null,
  showCustomEditor: false,
  showCustomGrid: false,
  query: "",
  lastValues: null,
  newData: false,

  width: 600,
  height: 800,
  fullscreen: false,
};
const pageParams: DataPageParameter = {
  zeitbereich: AUApi.Current.zeitbereich,
  insertMode: false,
  query: stammQuery,
  emitBack: () => emit("back"),
};
const uiData = reactive<DataPageUIData>(BaseSetup(pageParams));

function setSelectedStep(nextStep: number) {
  selecteditem.value = nextStep;
  step.value = nextStep;
}

watch(step, (nextStep) => {
  const found = uiData.tabItems?.find((t) => t.step === nextStep);
  if (found) {
    selecteditem.value = nextStep;
  }
});

onMounted(async () => {
  if (Constants.isMobile) {
    mobile.value = true;
  }
  initalized.value = false;
  if (siteData?.zeitbereich) {
    pageParams.zeitbereich = siteData.zeitbereich;
  }
  if (siteData?.stammQuery) {
    stammQuery = siteData.stammQuery;
    pageParams.query = stammQuery;
    neuDialog = siteData.neuDialog;
    auswahlQuery.value = siteData.auswahlQuery;
    neuErfassung.value = siteData.neuErfassung ?? true;
  }
  pageParams.bewegungsdaten = false;
  pageParams.backOnSave = false;
  if (siteData && siteData?.aic !== 0) {
    selected.value = siteData.aic;
    pageParams.queryAIC = siteData.aic;
    siteData.aic = 0;
  }
  // mountedBase nur aufrufen wenn KEIN selectedItem gesetzt ist.
  // Wenn selected gesetzt ist, wird die ComboBox beim Mounten @changeValue feuern
  // und dort valueChangedSelect → mountedBase aufrufen.
  if (!selected.value) {
    await mountedBase(uiData, pageParams, false);
    if (!uiData.showLoading && uiData.data) {
      dialogStt.query = neuDialog;
      await initialize();
      showData.value = true;
    }
  }
  uiData.showLoading = false;
  uiData.initialized = true;
});
async function initialize() {
  if (uiData.header.keinDel) {
    allowDelete.value = false;
  }
  if (uiData.header.keinNeu) {
    neuErfassung.value = false;
  }
  if (uiData.header?.keinStichtag) {
    // beim Stammtyp kann nun ebenfalls definiert werden ob ein Stichtag/Änderungsdatum angezeigt werden soll!
    showTimeField.value = false;
  }
  // Stamm Beleg KEIN Änderungsdatum
  if (uiData.header.kennung === "Web_StammBeleg_KI") {
    showTimeField.value = false;
  }
  if (uiData.header.kennung === "Web_Stammdaten_MA") {
    showUser.value = true;
  }
  if (showUser.value) {
    const avatarData = getKZValueDisplay(
      uiData.data[0],
      "avatar",
      uiData.columns,
    );
    if (avatarData) {
      if (avatarData.data) {
        avatar.value = avatarData.data;
      } else {
        await loadImage(uiData, pageParams, avatarData);
        avatar.value = uiData.avatarIcon ?? "";
      }
    }
  }
  stammBezeichnung.value = getKZValueDisplay(
    uiData.data[0],
    "name",
    uiData.columns,
  );
  if (!stammBezeichnung.value) {
    stammBezeichnung.value = getKennungValueDisplay(
      uiData.data[0],
      "BEZEICHNUNG",
      uiData.columns,
    );
  }
  stammBezeichnung.value =
    stammBezeichnung.value +
    " " +
    getKZValueDisplay(uiData.data[0], "name2", uiData.columns);
  if (stammKennung.value === "" || !stammKennung.value) {
    stammKennung.value = stammBezeichnung.value.replace(/\s+/g, "");
  }
  if (uiData.tabItems && uiData.tabItems.length > 0) {
    const selectedTab = lastSelectedTab.value;
    const currentTab = uiData.tabItems.find(
      (t) => t.step === selecteditem.value,
    );
    const restoredTab = uiData.tabItems.find((t) => t.step === selectedTab);
    setSelectedStep(
      restoredTab?.step ?? currentTab?.step ?? uiData.tabItems[0].step,
    );
  }
  const defColumsNoChange = uiData.columns.filter(
    (c: any) => c.kennung === "NOCHANGE",
  );
  const defColumsAllowDelete = uiData.columns.filter(
    (c: any) => c.meta.kz === "allowDelete",
  );
  // ist der Stammsatz zB von einem anderen Mandaten - dann darf er auch nciht gelöscht werden!
  if (defColumsNoChange) {
    const noChange = getValue(uiData.data[0], defColumsNoChange[0]);
    if (noChange) {
      readOnlyMutable.value = true;
    }
  }
  if (defColumsAllowDelete) {
    const allowDelete = getValue(uiData.data[0], defColumsAllowDelete[0]);
    if (!allowDelete) {
      readOnlyMutable.value = true;
    }
  }

  initalized.value = true;
}
async function deleteSttMain(selected: number) {
  dialogDelete.value = false;
  const res = await deleteStt(uiData, pageParams, selected);
  if (!res) {
    await reloadList();
  }
}
async function reloadList() {
  dialogDelete.value = false;
  comboKey.value += 1;
  selected.value = 0;
  valueChangedSelect({ aic: 0 });
}
let onClosed!: () => void | null;
function newItem(item: any) {
  dialogStt.aic = undefined;
  dialogStt.newData = true;
  dialogOpen.value = true;
}

function restoreLastSelectedTab() {
  const selectedTab = lastSelectedTab.value;
  if (selectedTab === null) {
    return;
  }

  const found = uiData.tabItems?.find((t) => t.step === selectedTab);
  if (found) {
    setSelectedStep(found.step);
  }
  lastSelectedTab.value = null;
}

async function onBack(updatedata?: any, reload?: boolean) {
  // Vor dem Reload Tab merken
  if (uiData.tabItems && uiData.tabItems.length > 0) {
    lastSelectedTab.value = step.value;
  }
  if (reload && !updatedata) {
    emitReload.value = true;
    uiData.showLoading = true;
    comboKey.value += 1;
    initalized.value = false;
    await mountedBase(uiData, pageParams);
    if (!uiData.showLoading && uiData.data) {
      dialogStt.query = neuDialog;
      await initialize();
    }
    uiData.showLoading = false;
    uiData.initialized = true;
  }
  if (updatedata || uiData.header.refreshNS) {
    uiData.showLoading = true;
    comboKey.value += 1;
    selected.value = updatedata?.aic_Stamm ?? selected.value;
    await valueChangedSelect(updatedata ?? { aic: selected.value });
    emitReload.value = true; // NACH valueChangedSelect setzen, da dort auf false gesetzt wird
  }

  dialogOpen.value = false;
  mini.value = true;
  // if (uiData.header.refreshNS) {
  //   // dann soll die gesamte maske neu gelden werden - weil anzeige in den tabs sich ändern kann (Bsp Belege)
  // }
  // Nach dem Reload Tab wiederherstellen
  restoreLastSelectedTab();
}
function changedMe(val: boolean) {
  // wenn grad geschlossen app informieren
  if (drawer) {
    wasopen = true;
  } else if (!drawer && onClosed && wasopen) {
    onClosed();
    wasopen = false;
  }
}
function onClose() {
  siteData.onReloadData = emitReload.value;
  siteData.reloadData = emitReload.value;
  // Prüfe, ob wir als SubPage laufen (z.B. aus BasePage/openSubPage)
  if (siteData && siteData.isSubPage) {
    goBack();
  } else {
    emit("back");
  }
}
async function valueChangedSelect(x: any) {
  if (saveFrage.value) {
    if (
      confirm(
        "Wollen Sie die Seite wirklich verlassen - es sind nicht alle Daten gespeichert?",
      )
    ) {
      saveFrage.value = false;
    } else {
      return;
    }
  }
  showData.value = true;
  pageParams.queryAIC = x.aic ?? x.aic_Stamm;
  selected.value = x.aic ?? x.aic_Stamm;
  emitReload.value = false;
  if (siteData && siteData?.aic !== 0) {
    siteData.aic = 0;
  }
  initalized.value = false;
  await mountedBase(uiData, pageParams);
  if (!uiData.showLoading && uiData.data) {
    dialogStt.query = neuDialog;
    await initialize();
  }
  uiData.showLoading = false;
  uiData.initialized = true;
}

function checkSite(item: any) {
  if (saveFrage.value || saveVisibleStt) {
    if (
      confirm(
        "Wollen Sie die Seite wirklich verlassen - es sind nicht alle Daten gespeichert?",
      )
    ) {
      saveFrage.value = false;
      setSelectedStep(item.step);
      // mini.value = true;
    }
  } else {
    setSelectedStep(item.step);
    mini.value = true;
  }
}
</script>
<template>
  <v-card fluid class="pa-0" v-if="uiData.initialized" :min-height="myHeight">
    <v-toolbar class="dvhBackground">
      <!-- <v-btn
        v-if="isMobile"
        size="small"
        color="white"
        :icon="mini ? 'mdi-menu-open' : 'mdi-menu'"
        variant="flat"
        class="ml-2"
        @click.stop="mini = !mini"
      /> -->
      <EditComboBox
        v-show="neuErfassung || allowDelete"
        :key="comboKey"
        ref="comboList"
        :query="auswahlQuery"
        class="pl-5 pt-4"
        style="width: 400px"
        :modelValue="stammBezeichnung"
        @changeValue="valueChangedSelect"
        @update:modelValue="stammBezeichnung = $event"
        textKennung="NAME"
        :selectedItem="selected"
        :noChange="true"
        :disableValueChangeTracker="true"
        hide-details
        :queryZeitbereich="pageParams.zeitbereich"
      />

      <v-spacer></v-spacer>
      <v-btn
        size="small"
        dark
        color="green"
        @click="newItem"
        icon="mdi-plus"
        class="mr-1"
        variant="flat"
        v-if="neuErfassung"
      >
      </v-btn>

      <v-btn
        v-if="selected && !readOnlyMutable && allowDelete"
        size="small"
        dark
        color="red"
        @click="dialogDelete = true"
        icon="mdi-minus"
        class="mr-1"
        variant="flat"
      >
      </v-btn>
      <v-btn
        color="white"
        fab
        size="small"
        @click="onClose"
        icon="mdi-close"
        variant="flat"
      >
      </v-btn>
    </v-toolbar>
    <v-dialog
      v-if="dialogOpen"
      ref="dialog"
      eager
      leave-absolute="true"
      :loading="dialogStt"
      v-model="dialogOpen"
      :width="dialogStt.width"
      :height="dialogStt.height"
      :fullscreen="dialogStt.fullscreen"
      class="noPaddingNoMargin"
    >
      <BaseEditor
        :query="dialogStt.query"
        @back="onBack"
        @save="onBack"
        :aic="dialogStt.aic"
        :siteData="{ zeitbereich: dialogStt.zeitbereich }"
        :showTitle="true"
        :lastValues="dialogStt.lastValues"
        :showDeleteButton="false"
        :backOnSave="true"
        :newData="dialogStt.newData"
        fieldCssClass="sm6"
        class="pa-5"
        :maxWidth="dialogStt.width ?? 400"
        :maxHeight="dialogStt.height ?? 800"
      />
    </v-dialog>
    <v-dialog v-model="dialogDelete" max-width="550px">
      <v-card class="ma-5">
        <div
          style="white-space: nowrap; width: 80%; text-align: center"
          class="text-h8 pa-3"
        >
          {{ frageDelete }}
        </div>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="blue darken-1"
            variant="text"
            @click="dialogDelete = false"
            prepend-icon="mdi-cancel"
          >
            {{ begriffBezeichnung("Abbruch") }}</v-btn
          >
          <v-btn
            color="red darken-1"
            variant="text"
            @click="deleteSttMain(selected)"
            prepend-icon="mdi-delete"
          >
            {{ begriffBezeichnung("Loeschen") }}</v-btn
          >
          <v-spacer></v-spacer>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-layout v-if="uiData.initialized && initalized">
      <v-navigation-drawer
        class="stamm-drawer"
        permanent
        v-model="drawer"
        :rail="mini"
        @transitionend="changedMe"
        height="100vh"
        style="z-index: 200"
        :mobile="mobile"
      >
        <v-list class="pa-0" density="compact" nav>
          <v-list-item
            v-if="showUser"
            class="SelectedTile"
            @click="showBenutzer = !showBenutzer"
            density="compact"
            active-class="dvhBackground lighten-2 white--text"
          >
            <template v-slot:prepend>
              <v-btn
                v-if="!avatar"
                variant="text"
                :title="stammBezeichnung"
                color="grey"
                icon="mdi-account-circle"
              >
              </v-btn>
              <v-avatar v-else size="32" class="ml-2 mt-2">
                <v-img :src="avatar" />
              </v-avatar>
            </template>
            <v-list-item-title>
              {{ stammBezeichnung }}
            </v-list-item-title>
          </v-list-item>
        </v-list>
        <v-dialog
          id="benutzer"
          v-if="showBenutzer && uiData.initialized"
          leave-absolute="true"
          v-model="showBenutzer"
        >
          <v-container grid-list-md text-xs-center class="pa-0">
            <BaseBenutzerMitarbeiter
              :stammsatz="pageParams.queryAIC"
              :stammBez="stammBezeichnung"
              :stammKen="stammKennung"
              @back="showBenutzer = false"
            ></BaseBenutzerMitarbeiter>
          </v-container>
        </v-dialog>
        <v-list class="pa-0" density="compact" v-if="initalized">
          <template v-for="(item, index) in uiData.tabItems" :key="item.step">
            <v-list-item
              nav
              dark
              density="compact"
              class="SelectedTile"
              :class="{ SelectedTileActive: selecteditem === item.step }"
              @click="checkSite(item)"
              :value="item.step"
            >
              <template v-slot:prepend>
                <v-badge
                  bordered
                  v-if="item.activeNumber"
                  color="green"
                  :content="item.activeNumber"
                  location="top end"
                >
                  <v-btn
                    v-if="!item.subsites"
                    variant="text"
                    :title="item.name"
                    :icon="item.icon"
                    :color="item.active ? item.color : 'grey'"
                  >
                  </v-btn>
                </v-badge>

                <v-btn
                  v-else
                  :title="item.name"
                  variant="text"
                  :icon="item.icon"
                  :color="item.active ? item.color : 'grey'"
                >
                </v-btn>
              </template>

              <v-list-item-title>
                {{ item.text }}
              </v-list-item-title>
            </v-list-item>
            <v-divider
              v-if="index < (uiData.tabItems?.length ?? 0) - 1"
              class="mx-2"
            />
          </template>
          <v-divider class="mx-2" />
          <v-btn
            :icon="mini ? 'mdi-chevron-right' : 'mdi-chevron-left'"
            variant="text"
            @click.stop="mini = !mini"
            color="grey"
          ></v-btn>
        </v-list>
      </v-navigation-drawer>

      <div v-if="!showData">bitte Stammsatz auswählen</div>

      <v-main v-if="showData && !uiData.showLoading" :min-height="myHeight">
        <v-toolbar :title="stammBezeichnung">
          <v-spacer></v-spacer>
          <SpeedDial
            v-if="uiData.printItems && uiData.printItems.length > 0"
            :aic="pageParams.queryAIC"
            :printItems="uiData.printItems"
            modell="0"
            modell1="0"
            :print="true"
            :edit="true"
            direction="left center"
          ></SpeedDial>
        </v-toolbar>

        <v-divider class="pt-3" v-if="!isMobile"></v-divider>
        <v-window v-model="step" class="window-item-fullheight">
          <v-window-item
            v-for="item in uiData.tabItems"
            :key="item.step"
            :value="item.step"
          >
            <component
              v-if="item.component"
              :is="item.component"
              :queryAic="pageParams.queryAIC"
              :mainAic="pageParams.queryAIC"
              :mainStt="uiData.header.aicStt"
              :query="item.query"
              :showBackButton="false"
              :showDeleteButton="false"
              :showSaveButton="!readOnlyMutable"
              :showTitle="false"
              class="pa-0 window-item-fullheight"
              :aic="pageParams.queryAIC"
              @changeValue="saveFrage = true"
              @save="saveFrage = false"
              :showTimeField="showTimeField"
              max-width="100%"
              :maxHeight="contentHeight"
              :modellItems="uiData.modelle"
              :printItems="uiData.printItems"
              :exportItems="uiData.exportItems"
              :zeitbereich="pageParams.zeitbereich"
              :backOnSave="true"
              @back="onBack"
              @onReloadData="emitReload = true"
            />
          </v-window-item>
        </v-window>
      </v-main>
    </v-layout>
  </v-card>
</template>

<style scoped>
.SelectedTile:hover {
  border-radius: 4px;
  background: green;
}

.SelectedTileActive {
  border-radius: 4px;
  background: #ff9800;
  color: white;
}

:deep(.SelectedTile .v-list-item__prepend) {
  min-width: 40px;
  width: 40px;
  justify-content: center;
}

:deep(.SelectedTile .v-badge__badge) {
  top: 4px !important;
  inset-inline-start: auto !important;
}

:deep(
  .stamm-drawer.v-navigation-drawer--rail:not(.v-navigation-drawer--is-hovering)
    .v-list-item-title
) {
  display: none;
}

.v-card {
  padding: 0 0 0 0;
  height: 100% !important;
}

.window-item-fullheight {
  height: 100% !important;
  min-height: 100% !important;
  margin-bottom: 20;
}
</style>
<style scoped>
.pl-5.pt-4,
.pl-5.pt-4 input,
.pl-5.pt-4 .v-field__input,
.pl-5.pt-4 .v-input__control,
.pl-5.pt-4 .v-select__selection {
  color: white !important;
}
</style>
